import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Box } from '@rsos/components/capstone/base';
import { ACCEPTED } from '@rsos/constants/alertStatuses';
import { CONVERSATION_ENDED } from '@rsos/constants/chatStatuses';
import useLoggedInUser from '@rsos/hooks/useLoggedInUser';
import {
  addPrivilegedParticipants,
  resetUnreadMessages,
} from '@rsos/reduxDataStore/src/features/alerts/chatSlice';
import { useCurrentConversation } from '@rsos/reduxDataStore/src/features/alerts/hooks';
import { addFollower } from '@rsos/reduxDataStore/src/features/alerts/incidentsSlice';
import { useSelectedAlert } from '@rsos/reduxDataStore/src/features/selectors/alertsSelectors';
import { captureException } from '@rsos/utils/sentry';
import {
  ALERT_PREFIX,
  CHAT_RAPTOR_PREFIX,
  createTalkSession,
  createTalkUser,
  getChatFilterPermissions,
} from '@rsos/utils/talkJS';

const HiddenComponent = styled.div`
  display: none;
`;

const CHAT_PERMISSIONS = {
  'permission.csp': 'False',
  'permission.icsp': 'False',
  'permission.midas_user': 'False',
  'permission.rsp': 'True',
};

/**
 * TalkJS message field visibility rules:
 * Show:
 * - alert status is ACCEPTED and Conversation Status is not CONVERSATION_ENDED
 * hide:
 * - alert is supplemental only
 * - alert status is NEW, DECLINED, TIMEOUT, CANCELED, IGNORED
 * - conversation status is CONVERSATION_ENDED
 * Do not check isChatEnabled. It is not used for Portal alerts, only iCSP
 * @param {String} alertStatus - alert status
 * @param {String} conversationStatus - alert conversation status
 * @returns Boolean
 */
const checkMessageFieldVisibility = ({ alertStatus, conversationStatus }) => {
  return (
    alertStatus === ACCEPTED &&
    !!conversationStatus &&
    conversationStatus !== CONVERSATION_ENDED
  );
};

/**
 * Create a TalkJS chat iframe container for alerts, when an alert status is accepted, the user can
 * participate in a conversation with other users in RSP and CSP.
 */
const TalkJSPrivilegedChatbox = ({ isChatBoxVisible }) => {
  const dispatch = useDispatch();

  const uiTheme = useSelector(state => state.sinatra.ui.theme);

  const profile = useSelector(state => state.sinatra.user.profile);

  const {
    account_id: currentPSAPAccountID,
    display_name: currentPSAPDisplayName,
  } = useSelector(state => state.psaps.currentPSAP);

  const selectedAlertID = useSelector(state => state.incidents.selectedAlertID);

  const chats = useSelector(state => state.chat.chats);

  const currentAlert = useSelectedAlert();

  const alertStatus = currentAlert?.status?.name;

  const alertConversation = useCurrentConversation();

  const alertConversationID = alertConversation?.privileged_conversation?.id;

  const alertConversationStatus =
    alertConversation?.privileged_conversation?.status;

  // TalkJS chat configuration
  const loggedInUser = useLoggedInUser('rsp_alerts_user');

  const [chatSession, setChatSession] = useState(null);
  const [inbox, setInbox] = useState(null);
  const [chatbox, setChatbox] = useState(null);
  const [openedAlerts, setOpenedAlerts] = useState(new Set());

  const chatRef = useRef();
  const inboxRef = useRef();

  const chatTheme = uiTheme === 'light' ? 'alerts' : 'alerts_dark';

  /**
   * TalkJS chatbox on change event handler for alerts
   * https://talkjs.com/docs/Reference/JavaScript_Chat_SDK/Chatbox.html#Chatbox.on__sendMessage__
   */
  const handleSendMessage = useCallback(
    event => {
      const permissions = CHAT_PERMISSIONS;

      event.override({
        custom: {
          entity_display_name: currentPSAPDisplayName || '',
          entity_id: currentPSAPAccountID || '',
          from_app: 'RSP',
          sender: loggedInUser.name,
          ...(selectedAlertID && {
            selectedAlertID,
            ...permissions,
          }),
        },
        text: event.message.text,
      });

      if (selectedAlertID && profile?.id) dispatch(addFollower(profile.id));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedAlertID],
  );

  /**
   * =========================================
   *  TALKJS CHATBOX & UNREADS INITIALIZATION
   * =========================================
   */

  // Create and mount the TalkJS session and chat container UI when the AlertPane is opened.
  // The TalkJS session and chat contaienr UI is destroyed when the AlertPane is closed.
  // https://talkjs.com/docs/Reference/JavaScript_Chat_SDK/Session/#Session__createChatbox
  // https://talkjs.com/docs/Reference/JavaScript_Chat_SDK/Chatbox/#Chatbox__mount
  useEffect(() => {
    let session, chat, inbox;

    try {
      (async () => {
        session = await createTalkSession(loggedInUser);

        inbox = session.createInbox();

        chat = session.createChatbox({
          theme: chatTheme,
          showChatHeader: false,
          messageField: {
            visible: false,
            placeholder: 'Type response',
          },
        });

        inbox.mount(inboxRef.current);
        chat.mount(chatRef.current);

        inbox.onConversationSelected(event => {
          if (event.participants) {
            const participantList = event.participants.filter(
              participant => participant.id !== event.me.id,
            );
            dispatch(addPrivilegedParticipants(participantList));
          }
        });

        setChatSession(session);
        setInbox(inbox);
        setChatbox(chat);
      })();
    } catch (error) {
      captureException(error);
    }

    return () => {
      if (session) {
        setChatbox(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatTheme]);

  /**
   * When minimizing a chat we want to deselect the conversation from the chatbox
   * while also keeping the conversation selected from the inbox.
   * Deselecting the conversation gives us ability to track unread messages and wipe on select.
   * Talkjs ONLY tracks participant list when Inbox selects a conversation so this needs to be selected for
   * initial load.
   * Optional Chaining clarification:
   * - chatSession.gOCC(undefined) - No errors, but TalkJS doesn't say what happens in this case so being safe here
   * - inbox.select(null) means that the conversation will be de-selected in the UI and the message list will disappear
   */
  useEffect(() => {
    if (!isChatBoxVisible) {
      chatbox?.select();
      let conversation;

      if (alertConversationID) {
        conversation = chatSession?.getOrCreateConversation(
          alertConversationID,
        );
      }

      if (conversation) {
        conversation.setParticipant(createTalkUser(loggedInUser));
        inbox?.select(conversation);
      } else {
        inbox?.select(null);
      }
    }
  }, [
    alertConversationID,
    chatSession,
    chatbox,
    inbox,
    isChatBoxVisible,
    loggedInUser,
  ]);

  /**
   * ========================
   *  ALERTS USEEFFECT HOOKS
   * ========================
   */

  // When switching between different alerts while the AlertPane is open, the TalkJS session and
  // chat container UI stays. The conversation is updated using the select() method and checks for
  // the message field visibility using the checkMessageFieldVisibility() util function.
  // Additionally, the selectedAlertID suffix and alertConversationID suffix must match so that
  // TalkJS can select the correction alert conversation from the backend.
  // https://talkjs.com/docs/Reference/JavaScript_Chat_SDK/Session/#Session__getOrCreateConversation
  // https://talkjs.com/docs/Reference/JavaScript_Chat_SDK/Chatbox/#Chatbox__select
  useEffect(() => {
    if (
      chatbox &&
      chatSession &&
      selectedAlertID &&
      alertConversationID &&
      selectedAlertID.split(ALERT_PREFIX)[1] ===
        alertConversationID.split(CHAT_RAPTOR_PREFIX)[1] &&
      isChatBoxVisible
    ) {
      const conversation = chatSession.getOrCreateConversation(
        alertConversationID,
      );

      conversation.setParticipant(createTalkUser(loggedInUser));

      chatbox.select(conversation);
      inbox.select(conversation);

      const isMessageFieldVisible = checkMessageFieldVisibility({
        alertStatus,
        conversationStatus: alertConversationStatus,
      });

      chatbox.messageField.setVisible(isMessageFieldVisible);

      const customFilter = getChatFilterPermissions();

      chatbox.setMessageFilter({
        custom: {
          ...customFilter,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    alertConversationID,
    alertConversationStatus,
    alertStatus,
    chatbox,
    chatSession,
    selectedAlertID,
    isChatBoxVisible,
  ]);

  // When onSendMessage is called it will stack the functions instead of overwritting them
  // To fix this, moving it into it's own useEffect should ensure that it gets called only once
  useEffect(() => {
    if (
      chatbox &&
      !openedAlerts.has(selectedAlertID) &&
      chatSession &&
      selectedAlertID &&
      alertConversationID &&
      selectedAlertID.split(ALERT_PREFIX)[1] ===
        alertConversationID.split(CHAT_RAPTOR_PREFIX)[1] &&
      isChatBoxVisible
    ) {
      setOpenedAlerts(new Set(openedAlerts.add(selectedAlertID)));

      chatbox.onSendMessage(event => {
        if (alertConversationID === event.conversation.id) {
          handleSendMessage(event);
        }
      });

      chatbox.onFocus(() => {
        dispatch(resetUnreadMessages(selectedAlertID));
      });
    }
  }, [
    alertConversationID,
    chatSession,
    chatbox,
    handleSendMessage,
    selectedAlertID,
    openedAlerts,
    chats,
    isChatBoxVisible,
    dispatch,
  ]);

  return (
    <>
      <HiddenComponent ref={inboxRef}></HiddenComponent>
      <Box width="100%" height="100%" ref={chatRef}></Box>
    </>
  );
};

export default TalkJSPrivilegedChatbox;
